import * as React from 'react';
import pxToRem from 'assets/theme/function/px-to-rem';
import {
    Avatar,
    Button,
    ListItemIcon,
    Menu,
    MenuItem,
    SvgIcon,
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import colors from 'assets/theme/base/colors';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import LogoutIcon from '@mui/icons-material/ExitToApp';
import { useEffect } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { useNavigate } from 'react-router-dom';
import { useSignOutMutation } from 'store/slices/staff-user/staff-user-auth-api';
import boxShadows from 'assets/theme/base/box-shadows';
import { ReactComponent as LogoIcon } from 'assets/images/logo-icon.svg';

const AccountMenu: React.FC = () => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [signOutApi, { isSuccess, error, isError }] = useSignOutMutation();

    const open = Boolean(anchorEl);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSignOut = async () => {
        const refreshToken: string =
            localStorage.getItem('refresh_token') !== null
                ? (localStorage.getItem('refresh_token') as string)
                : '';
        await signOutApi({ refreshToken: refreshToken });
        handleClose();
    };

    useEffect(() => {
        if (isSuccess) {
            navigate('/');
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError && error && 'data' in error && error?.data.message) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError, error, dispatch]);

    return (
        <>
            <Button
                disableRipple
                aria-controls={open ? 'user-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                endIcon={open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            >
                <Avatar
                    sx={{
                        bgcolor: colors.primary.main,
                        width: { md: pxToRem(45), xs: pxToRem(32) },
                        height: { md: pxToRem(45), xs: pxToRem(32) },
                    }}
                >
                    <LogoIcon />
                </Avatar>
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            width: { xs: pxToRem(150), sm: pxToRem(200) },
                            border: `1px solid ${colors.secondary.light}`,
                            boxShadow: boxShadows.tabsBoxShadow.indicator,
                        },
                    },
                }}
            >
                <MenuItem
                    onClick={() => {
                        dispatch(
                            openConfirmationDialogs({
                                textInfo: 'Are You Sure Want To Logout',
                                handleConfirm: handleSignOut,
                            }),
                        );
                        handleClose();
                    }}
                    sx={{ textTransform: 'capitalize', py: pxToRem(15) }}
                >
                    <ListItemIcon>
                        <SvgIcon
                            component={LogoutIcon}
                            inheritViewBox
                            sx={{
                                fill: colors.common.white,
                                fontSize: { xs: pxToRem(23), sm: pxToRem(25) },
                            }}
                        />
                    </ListItemIcon>
                    Logout
                </MenuItem>
            </Menu>
        </>
    );
};
export default AccountMenu;
